import React, { useEffect, useRef, useState } from "react";
import { getSettings } from "@/lib/api";

/**
 * AdSense — renders a Google AdSense unit configured through admin settings.
 * The publisher ID (ca-pub-XXXXXXXXXXXXXXX) is pulled from `settings.adsense_pub_id`
 * (Admin → Social & Profile). When empty, the component renders nothing so ad
 * blockers don't complain and the page stays clean.
 *
 * Usage:
 *   <AdSense slot="1234567890" format="fluid" />         // manual ad unit
 *   <AdSense slot="1234567890" format="auto" fullWidth />
 *
 * The AdSense script itself is loaded once, lazily, the first time an AdSense
 * component mounts (shared module-level state).
 */
let scriptPromise = null;
function loadScript(pubId) {
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise((resolve, reject) => {
    if (window.adsbygoogle) return resolve();
    const s = document.createElement("script");
    s.async = true;
    s.crossOrigin = "anonymous";
    s.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${pubId}`;
    s.onload = resolve;
    s.onerror = reject;
    document.head.appendChild(s);
  });
  return scriptPromise;
}

export default function AdSense({ slot, format = "auto", fullWidth = true, style, className = "" }) {
  const [cfg, setCfg] = useState(null);
  const insRef = useRef(null);

  useEffect(() => {
    getSettings().then((s) => setCfg({
      pubId: (s?.adsense_pub_id || "").trim() || null,
      blogSlot: (s?.adsense_blog_slot || "").trim() || null,
    })).catch(() => setCfg({}));
  }, []);

  const pubId = cfg?.pubId;
  // Placeholder slot falls back to the admin-configured blog slot; if neither
  // exists we render nothing (Auto Ads still covers the site via the global script).
  const effectiveSlot = slot && slot !== "0000000000" ? slot : cfg?.blogSlot;

  useEffect(() => {
    if (!pubId || !effectiveSlot || !insRef.current) return;
    loadScript(pubId).then(() => {
      try {
        (window.adsbygoogle = window.adsbygoogle || []).push({});
      } catch { /* AdSense pushes are idempotent-safe */ }
    }).catch(() => {});
  }, [pubId, effectiveSlot]);

  if (!pubId || !effectiveSlot) return null;

  return (
    <div className={`my-6 ${className}`} data-testid={`adsense-${slot || "auto"}`}>
      <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-1 text-center">Advertisement</p>
      <ins
        ref={insRef}
        className="adsbygoogle block"
        style={style || { display: "block", minHeight: "90px" }}
        data-ad-client={pubId}
        data-ad-slot={effectiveSlot}
        data-ad-format={format}
        data-full-width-responsive={fullWidth ? "true" : "false"}
      />
    </div>
  );
}
